import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useState, useRef, useEffect } from 'react';
import { fetchRuns } from '../api/client';
import type { ReactNode } from 'react';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function LayoutDashboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </svg>
  );
}

function ZapIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z" />
    </svg>
  );
}

function DatabaseSearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.693V5" />
      <path d="m22 22-1.875-1.875" />
      <path d="M3 12a9 3 0 0 0 8.697 2.998" />
      <path d="M3 5v14a9 3 0 0 0 9.28 2.999" />
      <circle cx="18" cy="18" r="3" />
      <ellipse cx="12" cy="5" rx="9" ry="3" />
    </svg>
  );
}

function BugIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2l1.88 1.88M16 2l-1.88 1.88M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z" />
      <path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5M6 13H2M20 13h-4M17.47 9c1.93-.2 3.53-1.9 3.53-4" />
    </svg>
  );
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Runs',
    items: [
      { label: 'Dashboard', to: '/runs',     icon: <LayoutDashboardIcon /> },
      { label: 'Trigger run', to: '/new-run', icon: <ZapIcon /> },
    ],
  },
  {
    title: 'Explorer',
    items: [
      { label: 'Test cases', to: '/test-cases', icon: <DatabaseSearchIcon /> },
      { label: 'Bugs',               to: '/bugs',       icon: <BugIcon /> },
    ],
  },
];

export function Sidebar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [pref, setCollapsed] = useState(() => { try { return localStorage.getItem('sidebar') === 'collapsed'; } catch { return false; } });
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 899px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const collapsed = pref || narrow;
  const [live, setLive] = useState(false);

  // Layout and Topbar read the sidebar width from this variable.
  useEffect(() => {
    document.documentElement.style.setProperty('--sb-w', collapsed ? '68px' : '220px');
    try { localStorage.setItem('sidebar', pref ? 'collapsed' : 'expanded'); } catch { /* blocked storage */ }
  }, [collapsed, pref]);

  // Live dot is decoration: ignore fetch errors.
  useEffect(() => {
    fetchRuns().then(r => setLive(r.some(x => x.running))).catch(() => undefined);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  return (
    <aside
      className={`sb${collapsed ? ' sb-collapsed' : ''}`}
      style={{
        width: 'var(--sb-w, 220px)',
        minWidth: 'var(--sb-w, 220px)',
        height: '100dvh',
        background: 'var(--c-panel)',
        borderRight: '1px solid var(--c-b2)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 40,
        overflowY: 'auto',
      }}
    >
      {/* Logo */}
      <button
        onClick={() => navigate('/runs')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 20px',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <img src="/fivetran-med-logo.svg" alt="Fivetran" style={{ height: '28px', display: 'block' }} />
      </button>

      {/* Nav */}
      <nav aria-label="Main" style={{ padding: '8px 0', flex: 1, overflowY: 'auto' }}>
        {NAV_SECTIONS.map(section => (
          <div key={section.title} style={{ marginBottom: '4px' }}>
            <div
              className="sb-title"
              style={{
                padding: '8px 20px 4px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--c-t6)',
              }}
            >
              {section.title}
            </div>
            {section.items.map(item => (
              <NavLink key={item.to} to={item.to} className="nav-link" title={collapsed ? item.label : undefined}>
                <span className="nav-ico">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.to === '/runs' && live && <span className="nav-live" role="status" aria-label="A run is in progress" />}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <button type="button" className="sb-toggle" onClick={() => setCollapsed(c => !c)} aria-pressed={collapsed} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}><path d="m11 17-5-5 5-5M18 17l-5-5 5-5" /></svg>
        <span className="nav-label">Collapse</span>
      </button>

      {/* User menu — bottom of sidebar */}
      {user && (
        <div
          ref={userMenuRef}
          style={{ position: 'relative', borderTop: '1px solid var(--c-b2)', padding: '12px 14px' }}
        >
          {/* Popup — opens upward */}
          {userMenuOpen && (
            <div
              style={{
                // Collapsed: the 68px rail is too narrow (and clips), so float the menu beside it.
                ...(collapsed
                  ? { position: 'fixed', bottom: '12px', left: '76px', width: '220px' }
                  : { position: 'absolute', bottom: 'calc(100% + 6px)', left: '10px', right: '10px' }),
                background: 'var(--c-overlay)',
                border: '1px solid var(--c-b4)',
                borderRadius: '10px',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.45)',
                overflow: 'hidden',
                zIndex: 100,
              }}
            >
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--c-b2)' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--c-t0)' }}>
                  {user.name || 'Unknown'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--c-t6)', marginTop: '2px', wordBreak: 'break-all' }}>
                  {user.email}
                </div>
              </div>
              <button
                onClick={() => { setUserMenuOpen(false); logout(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', textAlign: 'left',
                  padding: '11px 16px',
                  fontSize: '13px', color: 'var(--c-t3)',
                  background: 'transparent',
                  border: 'none', cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-b1)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          )}

          {/* Trigger row */}
          <button
            onClick={() => setUserMenuOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%',
              background: userMenuOpen ? 'var(--c-b1)' : 'transparent',
              border: userMenuOpen ? '1px solid var(--c-b4)' : '1px solid transparent',
              borderRadius: '8px',
              padding: '7px 8px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--c-b4)', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700, color: 'var(--c-t0)',
              }}
            >
              {initials(user.name || user.email)}
            </div>
            <div className="sb-user-text" style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--c-t0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name || 'Unknown'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--c-t6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            </div>
          </button>
        </div>
      )}
    </aside>
  );
}
